import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Sparkles, Send, RefreshCw, Trash2, FileText, MessageSquare, User, Bot, Wand2, Paperclip, X as XIcon } from "lucide-react";

// Markdown-ish renderer (tabolar + kalın + liste)
function renderMd(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listBuf = [];
  let tableBuf = null;

  const flushList = () => {
    if (listBuf.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} className="list-disc pl-6 space-y-1 my-2">
        {listBuf.map((l, i) => <li key={i} className="text-sm">{fmtInline(l)}</li>)}
      </ul>);
      listBuf = [];
    }
  };
  const flushTable = () => {
    if (tableBuf) {
      blocks.push(
        <div key={`t-${blocks.length}`} className="my-3 overflow-x-auto">
          <table className="text-xs border border-[#E5E5E5]">
            <thead className="bg-[#FAFAFA]">
              <tr>{tableBuf.header.map((h, i) => <th key={i} className="px-3 py-1.5 text-left font-semibold uppercase tracking-widest text-neutral-500 border-b border-[#E5E5E5]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {tableBuf.rows.map((r, ri) => (
                <tr key={ri} className="border-t border-[#F5F5F5]">
                  {r.map((c, ci) => <td key={ci} className="px-3 py-1.5">{fmtInline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableBuf = null;
    }
  };

  const fmtInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Table detection: header row followed by separator |---|
    if (line.includes("|") && lines[i+1] && /^\s*\|?\s*-+/.test(lines[i+1])) {
      flushList();
      const parseRow = (row) => row.split("|").map(c => c.trim()).filter((c, idx, arr) => !(idx === 0 && !c) && !(idx === arr.length - 1 && !c));
      const header = parseRow(line);
      i++; // skip separator
      const rows = [];
      while (i + 1 < lines.length && lines[i+1].includes("|")) {
        i++;
        rows.push(parseRow(lines[i]));
      }
      tableBuf = { header, rows };
      flushTable();
      continue;
    }
    // Heading
    const h2 = /^##\s+(.*)/.exec(line);
    const h3 = /^###\s+(.*)/.exec(line);
    if (h2) { flushList(); flushTable(); blocks.push(<h3 key={`h-${blocks.length}`} className="font-heading font-bold text-lg mt-4 mb-2">{fmtInline(h2[1])}</h3>); continue; }
    if (h3) { flushList(); flushTable(); blocks.push(<h4 key={`h-${blocks.length}`} className="font-heading font-bold text-base mt-3 mb-1.5 text-[#0020C2]">{fmtInline(h3[1])}</h4>); continue; }
    // List item
    const li = /^\s*[-*]\s+(.*)/.exec(line);
    if (li) { listBuf.push(li[1]); continue; }
    flushList();
    if (line.trim() === "") { blocks.push(<div key={`br-${blocks.length}`} className="h-2" />); continue; }
    blocks.push(<p key={`p-${blocks.length}`} className="text-sm leading-relaxed my-1.5">{fmtInline(line)}</p>);
  }
  flushList(); flushTable();
  return blocks;
}

export default function AiAssistantPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tab, setTab] = useState("chat"); // chat | report
  const [file, setFile] = useState(null);
  const boxRef = useRef(null);

  const sendFile = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    const q = input.trim();
    const fd = new FormData();
    fd.append("file", file);
    fd.append("question", q);
    if (sessionId) fd.append("session_id", sessionId);
    // Show user turn immediately
    setMessages(prev => [...prev, { role: "user", content: `📎 **${file.name}**\n${q || "(Genel analiz iste)"}` }]);
    setInput(""); setFile(null); setSending(true);
    try {
      const { data } = await api.post("/ai/analyze-file", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (!sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
      toast.success(`${data.kind} okundu (${data.content_length} karakter analiz edildi)`);
      loadSessions();
    } catch (e) {
      toast.error(errMsg(e));
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ Hata: ${errMsg(e)}` }]);
    } finally { setSending(false); }
  };

  const loadSessions = async () => {
    try { const { data } = await api.get("/ai/sessions"); setSessions(data || []); } catch (e) { /* silent */ }
  };
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages]);

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: msg, include_snapshot: true, year });
      if (!sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
      loadSessions();
    } catch (e) {
      toast.error(errMsg(e));
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ Hata: ${errMsg(e)}` }]);
    } finally { setSending(false); }
  };

  const startNewChat = () => { setSessionId(null); setMessages([]); };

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/ai/sessions/${sid}`);
      setSessionId(sid);
      setMessages((data.messages || []).map(m => ({ role: m.role, content: m.content })));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Bu konuşma silinsin mi?")) return;
    try { await api.delete(`/ai/sessions/${sid}`); if (sid === sessionId) startNewChat(); loadSessions(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const generateReport = async () => {
    setReportLoading(true);
    setReport(null);
    try {
      const { data } = await api.post("/ai/health-report", { year });
      setReport(data);
      toast.success("Rapor hazır");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReportLoading(false); }
  };

  const quickPrompts = [
    "Bu yıl en çok gider yapan 5 kalem hangileri, hangilerini azaltabiliriz?",
    "Şoför maaşlarım araç kazançlarımla dengeli mi? Zarar ettiğim şoförler var mı?",
    "Vadesi geçmiş alacaklarım için tahsilat stratejisi öner",
    "Yakıt giderim yıla göre artmış mı, sebebi ne olabilir?",
    "Öğrenci ödemelerini faturaya bağlama oranımız düşük, nasıl artırırız?",
    "Hangi tedarikçilere en çok borcum var, ödeme planı öner",
  ];

  return (
    <>
      <PageHeader
        title="AI Danışman"
        subtitle="Yapay zeka şirket verilerinizi analiz eder, sorularınızı yanıtlar, aksiyon önerileri sunar (Claude Sonnet 4.5)"
        testid="ai-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="ai-year" value={year} onChange={e => setYear(Number(e.target.value))} className="px-3 py-2 text-xs border border-[#E5E5E5] font-semibold">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }
      />

      {/* Mode tabs */}
      <div className="flex items-center gap-1 border-b border-[#E5E5E5] mb-4">
        <button data-testid="tab-chat" onClick={() => setTab("chat")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-b-2 -mb-px ${tab === "chat" ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}>
          <MessageSquare className="w-3.5 h-3.5" /> Chat (Soru-Cevap)
        </button>
        <button data-testid="tab-report" onClick={() => setTab("report")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-b-2 -mb-px ${tab === "report" ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}>
          <FileText className="w-3.5 h-3.5" /> Şirket Sağlık Raporu
        </button>
      </div>

      {/* CHAT MODE */}
      {tab === "chat" && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Sessions sidebar */}
          <div className="md:col-span-1 bg-white border border-[#E5E5E5] rounded-md">
            <div className="px-3 py-2.5 border-b border-[#E5E5E5] flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Konuşmalar</span>
              <button data-testid="new-chat-btn" onClick={startNewChat} className="text-[10px] uppercase tracking-widest text-[#0020C2] font-semibold hover:underline">
                + Yeni
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {sessions.length === 0 && (
                <div className="p-4 text-xs text-neutral-500 text-center">Henüz konuşma yok. Aşağıdan sorunuzu yazın.</div>
              )}
              {sessions.map(s => (
                <div key={s.session_id} onClick={() => loadSession(s.session_id)}
                  className={`px-3 py-2.5 border-b border-[#F5F5F5] cursor-pointer hover:bg-[#FAFAFA] ${s.session_id === sessionId ? "bg-[#F5F5F5]" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold truncate flex-1">{s.first_message || "(Boş)"}</div>
                    <button onClick={e => { e.stopPropagation(); deleteSession(s.session_id); }} className="p-1 hover:bg-[#DC2626] hover:text-white flex-shrink-0"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1">{s.message_count} mesaj</div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat panel */}
          <div className="md:col-span-3 bg-white border border-[#E5E5E5] rounded-md flex flex-col" style={{ height: "70vh" }}>
            <div ref={boxRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkles className="w-10 h-10 text-[#0020C2] mx-auto mb-3" />
                  <div className="font-heading font-bold text-lg mb-2">AI Danışmanınızla Konuşun</div>
                  <div className="text-xs text-neutral-500 mb-6 max-w-md mx-auto">Şirket verilerinizi otomatik yükler, sorularınıza rakamlarla cevap verir. Örnek sorular:</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl mx-auto">
                    {quickPrompts.map((qp, i) => (
                      <button key={i} onClick={() => setInput(qp)} data-testid={`quick-prompt-${i}`}
                        className="text-left text-xs px-3 py-2 border border-[#E5E5E5] hover:border-[#0020C2] hover:bg-[#F5F5F5] transition-colors">
                        <Wand2 className="w-3 h-3 inline-block mr-1.5 text-[#0020C2]" />{qp}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                  {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-[#0020C2] text-white flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4" /></div>}
                  <div className={`max-w-[80%] px-4 py-2.5 ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5]"}`}>
                    {m.role === "user" ? <div className="text-sm">{m.content}</div> : renderMd(m.content)}
                  </div>
                  {m.role === "user" && <div className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center flex-shrink-0"><User className="w-4 h-4" /></div>}
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#0020C2] text-white flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4" /></div>
                  <div className="px-4 py-2.5 bg-[#F5F5F5] text-sm text-neutral-500 flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> Düşünüyor...</div>
                </div>
              )}
            </div>
            <div className="border-t border-[#E5E5E5] p-3">
              {file && (
                <div className="mb-2 flex items-center gap-2 text-xs bg-[#DBEAFE] border border-[#0020C2] px-3 py-1.5">
                  <Paperclip className="w-3.5 h-3.5 text-[#0020C2]" />
                  <span className="flex-1 truncate">
                    <strong>{file.name}</strong> · {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <button onClick={() => setFile(null)} title="Kaldır" className="p-1 hover:bg-white"><XIcon className="w-3 h-3" /></button>
                </div>
              )}
              <div className="flex gap-2">
                <label data-testid="ai-file-btn" className="cursor-pointer inline-flex items-center gap-1 px-3 py-2 border border-[#E5E5E5] text-xs font-semibold hover:bg-[#F5F5F5]" title="Excel veya PDF dosyası yükle">
                  <Paperclip className="w-3.5 h-3.5" />
                  <input data-testid="ai-file-input" type="file" accept=".xlsx,.xls,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
                </label>
                <input data-testid="ai-input" value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); file ? sendFile() : sendMessage(); } }}
                  placeholder={file ? "Dosya için soru (opsiyonel)..." : "Sorunuzu yazın... (Enter ile gönder)"}
                  disabled={sending}
                  className="flex-1 px-3 py-2 text-sm border border-[#E5E5E5] focus:border-[#0020C2] outline-none" />
                <button data-testid="ai-send" onClick={file ? sendFile : sendMessage} disabled={sending || (!file && !input.trim())}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0] disabled:opacity-40 flex items-center gap-2">
                  <Send className="w-3.5 h-3.5" /> {file ? "Analiz Et" : "Gönder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REPORT MODE */}
      {tab === "report" && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E5E5] rounded-md p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="max-w-2xl">
                <div className="font-heading font-bold text-lg mb-1">Otomatik Şirket Sağlık Raporu</div>
                <div className="text-xs text-neutral-500">{year} yılı verilerinizi (fatura, ödeme, borç, alacak, şoför performansı, yakıt, maaş, öğrenci ödemeleri) analiz eder. Kırmızı bayrakları, fırsatları ve aksiyon planı listeler.</div>
              </div>
              <button data-testid="gen-report-btn" onClick={generateReport} disabled={reportLoading}
                className="px-5 py-3 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0] disabled:opacity-40 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {reportLoading ? "Analiz ediliyor..." : "Raporu Oluştur"}
              </button>
            </div>
          </div>

          {reportLoading && (
            <div className="bg-white border border-[#E5E5E5] rounded-md p-16 text-center">
              <RefreshCw className="w-8 h-8 text-[#0020C2] mx-auto mb-3 animate-spin" />
              <div className="font-heading font-bold text-sm mb-1">AI verilerinizi inceliyor</div>
              <div className="text-xs text-neutral-500">Bu işlem 15-45 saniye sürebilir. Lütfen bekleyin...</div>
            </div>
          )}

          {report && (
            <div className="bg-white border border-[#E5E5E5] rounded-md">
              <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{report.year} Yılı</div>
                  <div className="font-heading font-bold text-lg">Şirket Sağlık Raporu</div>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(report.report); toast.success("Kopyalandı"); }}
                  className="px-3 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                  Kopyala
                </button>
              </div>
              <div className="p-6 max-w-5xl">
                {renderMd(report.report)}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
